import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Package } from 'lucide-react';
import { useFrappeFileUrl } from '@/lib/utils/frappeUrl';
import { frappeMethod } from '@/lib/erp/utils';
import { cn } from '@/lib/utils';
import { formatCurrency } from './formatCurrency';
import type { TemplateVariantsResponse } from './types';

interface VariantSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateItemCode: string;
  onSelectVariant: (itemCode: string) => void;
  currency: string;
}

/**
 * Variant Selector Modal - e-commerce style attribute picker
 */
export function VariantSelectorModal({
  open,
  onOpenChange,
  templateItemCode,
  onSelectVariant,
  currency,
}: VariantSelectorModalProps) {
  const toAbsolute = useFrappeFileUrl();
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});

  // Fetch template variants data
  const { data, isLoading } = useQuery({
    queryKey: ['template-variants', templateItemCode],
    queryFn: () =>
      frappeMethod<TemplateVariantsResponse>(
        'zaviago_backend.api.item_variants.get_template_variants_for_selection',
        { template_item_code: templateItemCode }
      ),
    enabled: open && !!templateItemCode,
  });

  // Find matching variant based on selected attributes
  const matchingVariant = useMemo(() => {
    if (!data?.variants || Object.keys(selectedAttributes).length === 0) return null;

    const attributeNames = Object.keys(data.attribute_options);
    const allSelected = attributeNames.every((attr) => selectedAttributes[attr]);
    if (!allSelected) return null;

    return data.variants.find((variant) => {
      return attributeNames.every(
        (attr) => variant.attributes[attr] === selectedAttributes[attr]
      );
    });
  }, [data, selectedAttributes]);

  // Get image to display (matching variant or template)
  const displayImage = useMemo(() => {
    if (matchingVariant?.image) return toAbsolute(matchingVariant.image);
    if (data?.template?.image) return toAbsolute(data.template.image);
    return null;
  }, [matchingVariant, data, toAbsolute]);

  // Count selected attributes
  const selectedCount = Object.keys(selectedAttributes).length;
  const totalAttributes = data?.attribute_options ? Object.keys(data.attribute_options).length : 0;

  const handleAttributeSelect = useCallback((attribute: string, value: string) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [attribute]: value,
    }));
  }, []);

  const handleAddToCart = useCallback(() => {
    if (matchingVariant) {
      onSelectVariant(matchingVariant.item_code);
      onOpenChange(false);
      setSelectedAttributes({});
    }
  }, [matchingVariant, onSelectVariant, onOpenChange]);

  // Reset selection when modal closes
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setSelectedAttributes({});
      }
      onOpenChange(newOpen);
    },
    [onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="text-lg leading-snug">{data?.template?.item_name || 'Select Options'}</DialogTitle>
          <DialogDescription>
            {totalAttributes > 0
              ? `Select ${totalAttributes} option${totalAttributes > 1 ? 's' : ''} to add to cart`
              : 'Choose your preferred options'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="px-6 py-12 text-center text-muted-foreground">
            <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            Loading options...
          </div>
        ) : !data ? (
          <div className="px-6 py-12 text-center text-muted-foreground">
            Failed to load options
          </div>
        ) : (
          <>
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Product Image - Full width hero style */}
              <div className="px-6 pt-4 pb-6">
                <div className="aspect-square w-full max-w-[200px] mx-auto rounded-md overflow-hidden bg-muted flex items-center justify-center">
                  {displayImage ? (
                    <img src={displayImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Package className="w-16 h-16 text-muted-foreground/30" />
                  )}
                </div>
              </div>

              {/* Attribute Selectors */}
              <div className="px-6 pt-2 pb-4 space-y-5">
                {Object.entries(data.attribute_options).map(([attribute, options]) => {
                  const isSelected = !!selectedAttributes[attribute];
                  return (
                    <div key={attribute} className="space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-semibold text-foreground">{attribute}</label>
                        {isSelected && (
                          <span className="text-xs text-[#007AFF] font-medium">
                            {selectedAttributes[attribute]}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {options.map((option) => {
                          const optionSelected = selectedAttributes[attribute] === option;
                          return (
                            <button
                              key={option}
                              onClick={() => handleAttributeSelect(attribute, option)}
                              className={cn(
                                'px-4 py-2.5 rounded text-sm font-medium transition-all duration-150',
                                optionSelected
                                  ? 'bg-[#007AFF] text-white shadow-sm'
                                  : 'bg-muted/60 hover:bg-muted text-foreground'
                              )}
                            >
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer with Price and Add Button — fixed at bottom */}
            <div className="px-6 pb-6 pt-2 space-y-4 border-t border-border/50 shrink-0">
              {/* Price Row */}
              <div className="flex items-center justify-between pt-4">
                <div className="flex flex-col">
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                    Price
                  </span>
                  <span className="text-2xl font-semibold text-foreground">
                    {matchingVariant
                      ? formatCurrency(matchingVariant.standard_rate, currency)
                      : '—'}
                  </span>
                </div>
                {matchingVariant && (
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground">{matchingVariant.item_code}</span>
                  </div>
                )}
              </div>

              {/* Add to Cart Button */}
              <Button
                className="w-full h-12 rounded bg-[#007AFF] hover:bg-[#007AFF]/90 text-white text-base font-semibold shadow-sm"
                disabled={!matchingVariant}
                onClick={handleAddToCart}
              >
                {matchingVariant ? (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </>
                ) : (
                  `Select ${totalAttributes - selectedCount} more option${totalAttributes - selectedCount > 1 ? 's' : ''}`
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
